import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const FloatingMenu = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hideTimeout, setHideTimeout] = useState<NodeJS.Timeout | null>(null);
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      
      // 200px以上スクロールしたら表示
      if (scrollY > 200) {
        setIsVisible(true);
        
        // 既存のタイムアウトをクリア
        if (hideTimeout) {
          clearTimeout(hideTimeout);
        }
        
        // 新しいタイムアウトを設定（3秒後に隠れる）
        const timeout = setTimeout(() => {
          setIsVisible(false);
          setIsMenuOpen(false);
        }, 3000);
        
        setHideTimeout(timeout);
      } else {
        setIsVisible(false);
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }
    };
  }, [hideTimeout]);

  const handleMouseEnter = () => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
    }
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setIsVisible(false);
      setIsMenuOpen(false);
    }, 2000);
    setHideTimeout(timeout);
  };

  const handleNavClick = (sectionId: string) => {
    setIsMenuOpen(false);
    
    if (location.pathname !== '/') {
      window.REACT_APP_NAVIGATE('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const menuItems = [
    { icon: 'ri-home-line', label: i18n.language === 'ja' ? 'ホーム' : 'Home', action: () => {
      setIsMenuOpen(false);
      if (location.pathname !== '/') {
        window.REACT_APP_NAVIGATE('/');
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }},
    { icon: 'ri-team-line', label: i18n.language === 'ja' ? 'メンバー' : 'People', action: () => handleNavClick('people') },
    { icon: 'ri-article-line', label: 'Live Log', action: () => handleNavClick('live-log') },
    { icon: 'ri-user-add-line', label: i18n.language === 'ja' ? '参加する' : 'Join', action: () => handleNavClick('join') },
    { icon: 'ri-play-circle-line', label: 'Media', action: () => handleNavClick('media') },
    { icon: 'ri-information-line', label: 'About', action: () => handleNavClick('about') },
  ];

  return (
    <>
      {/* フローティングハンバーガーボタン */}
      <div
        className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="w-14 h-14 bg-gray-900 hover:bg-gray-800 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
          aria-label="メニュー"
        >
          <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl transition-transform duration-300`}></i>
        </button>

        {/* メニューパネル */}
        <div
          className={`absolute bottom-20 right-0 bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${
            isMenuOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'
          }`}
          style={{ minWidth: '200px' }}
        >
          <div className="py-2">
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={item.action}
                className="w-full px-5 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className={`${item.icon} text-xl text-gray-700`}></i>
                </div>
                <span className="text-sm font-medium text-gray-900 whitespace-nowrap">{item.label}</span>
              </button>
            ))}
            
            {/* 相談するボタン（目立つデザイン） */}
            <div className="px-3 pt-2 pb-3">
              <button
                onClick={() => handleNavClick('gateway')}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-5 py-3 rounded-xl font-bold text-sm transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-chat-smile-3-line text-lg"></i>
                <span>{i18n.language === 'ja' ? '相談する' : 'Ask'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FloatingMenu;
